import chain from '@/lib/utils/chain';
import {
  EDITOR_CANVAS_WIDTH,
  EDITOR_CANVAS_GRID_MARGIN,
  EDITOR_CANVAS_GRID_COL_NUM,
  EDITOR_CANVAS_GRID_ROW_HEIGHT,
} from '@/lib/constants';

export function bottomYPosition(modules) {
  return chain(modules)
    .map((mod) => mod.layout.y_position + mod.layout.height)
    .concat([0])
    .max()
    .value();
}

/**
 * Assigns unique z_positions to the provided modules in order.
 * @param {Array} modules Modules to assign z_positions to.
 */
export function assignUniqueZPositions(modules) {
  return modules.map((mod, index) => ({
    ...mod,
    layout: {
      ...mod.layout,
      z_position: index + 1,
    },
  }));
}

/**
 * Converts width in grid columns to pixels including margins in between.
 * @param {Number} value Width in grid columns
 */
export function widthToPixels(value) {
  const totalMarginsWidth = (EDITOR_CANVAS_GRID_COL_NUM + 1) * EDITOR_CANVAS_GRID_MARGIN;
  const singleColumnWidth = (EDITOR_CANVAS_WIDTH - totalMarginsWidth) / EDITOR_CANVAS_GRID_COL_NUM;
  const marginBetweenColumns = (value - 1) * EDITOR_CANVAS_GRID_MARGIN;
  return marginBetweenColumns + singleColumnWidth * value;
}

/**
 * Converts height in grid rows to pixels including margins in between.
 * @param {Number} value Height in rows.
 */
export function heightToPixels(value) {
  const marginBetweenRows = (value - 1) * EDITOR_CANVAS_GRID_MARGIN;
  return EDITOR_CANVAS_GRID_ROW_HEIGHT * value + marginBetweenRows;
}

/**
 * Converts x_position in grid columns to pixels.
 * @param {Number} value x_position in grid columns
 */
export function xPositionToPixels(value) {
  const totalMarginsWidth = (EDITOR_CANVAS_GRID_COL_NUM + 1) * EDITOR_CANVAS_GRID_MARGIN;
  const singleColumnWidth = (EDITOR_CANVAS_WIDTH - totalMarginsWidth) / EDITOR_CANVAS_GRID_COL_NUM;
  const marginPixels = (value + 1) * EDITOR_CANVAS_GRID_MARGIN;
  return marginPixels + value * singleColumnWidth;
}

/**
 * Converts y_position in grid rows to pixels.
 * @param {Number} value y_position in grid rows
 */
export function yPositionToPixels(value) {
  const marginPixels = (value + 1) * EDITOR_CANVAS_GRID_MARGIN;
  return EDITOR_CANVAS_GRID_ROW_HEIGHT * value + marginPixels;
}

/**
 * Converts module layout from rows/columns to pixels.
 * @param {Object} layout - module layout
 */
export function layoutToPixels(layout, zPosition) {
  return {
    ...layout,
    height: heightToPixels(layout.height),
    width: widthToPixels(layout.width),
    x_position: xPositionToPixels(layout.x_position),
    y_position: yPositionToPixels(layout.y_position),
    z_position: zPosition || 0.5,
  };
}

/**
 * Converts a height in pixels to grid rows.
 * @param {Number} heightInPixels Height in pixels
 */
export function pixelsToHeight(heightInPixels) {
  const rowHeight = EDITOR_CANVAS_GRID_ROW_HEIGHT + EDITOR_CANVAS_GRID_MARGIN;
  let rowCount = Math.floor(heightInPixels / rowHeight);
  if (heightInPixels % rowHeight === 0) rowCount -= 1;
  const rowsHeightPixels = rowCount * rowHeight + EDITOR_CANVAS_GRID_ROW_HEIGHT;
  rowCount = heightInPixels <= rowsHeightPixels ? rowCount + 1 : rowCount + 2;
  return rowCount;
}

/**
 * Reformats the layout data stored in Mongo into the format
 * expected by the grid library
 * @param {Object} mod - Module data from the store
 */
export function mapModuleToGridItem(mod) {
  // if missing, apply default values
  return {
    id: mod.id,
    x: mod.layout.x_position || 0,
    y: mod.layout.y_position || 0,
    z: mod.layout.z_position || 0,
    height: mod.layout.height || 350,
    width: mod.layout.width || 350,
    rotation: mod.layout.rotate_degree || 0,
  };
}

/**
 * Gets the bounding box for the given array of points.
 */
export function getBoundingBox(points) {
  if (!points?.length) {
    return {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width: 0,
      height: 0,
    };
  }
  const xValues = points.map((point) => point.x);
  const yValues = points.map((point) => point.y);
  const left = Math.min(...xValues);
  const right = Math.max(...xValues);
  const top = Math.min(...yValues);
  const bottom = Math.max(...yValues);

  return {
    left,
    right,
    top,
    bottom,
    width: right - left,
    height: bottom - top,
  };
}

/**
 * Returns true if the two bounding boxes overlap each other.
 */
export function boundingBoxesOverlap(boxOne, boxTwo) {
  const xAxisOverlap = (boxOne.left >= boxTwo.left && boxOne.left <= boxTwo.right)
    || (boxOne.right >= boxTwo.left && boxOne.right <= boxTwo.right)
    || (boxTwo.left >= boxOne.left && boxTwo.left <= boxOne.right)
    || (boxTwo.right >= boxOne.left && boxTwo.right <= boxOne.right);
  const yAxisOverlap = (boxOne.top >= boxTwo.top && boxOne.top <= boxTwo.bottom)
    || (boxOne.bottom >= boxTwo.top && boxOne.bottom <= boxTwo.bottom)
    || (boxTwo.top >= boxOne.top && boxTwo.top <= boxOne.bottom)
    || (boxTwo.bottom >= boxOne.top && boxTwo.bottom <= boxOne.bottom);
  return xAxisOverlap && yAxisOverlap;
}

export default {
  bottomYPosition,
  assignUniqueZPositions,
  widthToPixels,
  heightToPixels,
  xPositionToPixels,
  yPositionToPixels,
  layoutToPixels,
  pixelsToHeight,
  getBoundingBox,
  boundingBoxesOverlap,
};
